// Pixel icons: designed profile pictures and space symbols on a 7×7 grid, the same grid as
// the generated marks (a 5×5 mirrored pattern with a one-cell margin). Text form, shared
// with the server (hrth_proto::pixels): `px1;7;<colour>,…;<49 hex digits>`, 0 = empty,
// n = the n-th colour. Icons carry their colours, so they survive palette changes. The
// colour `ink` is monochrome: the theme's ink (white on dark, black on light), always there.

export const INK = 'ink'
import { identicon } from './pattern'

export const SIDE = 7

export interface PixelIcon {
  colors: string[]
  /** SIDE*SIDE cells: 0 empty, else 1-based index into `colors`. */
  cells: number[]
}

export function parsePixels(s: string | null | undefined): PixelIcon | null {
  const m = /^px1;7;([^;]*);([0-9a-fA-F]{49})$/.exec(s ?? '')
  if (!m) return null
  const colors = m[1]!.split(',').filter(Boolean).map((c) => c.toLowerCase())
  if (!colors.length || colors.length > 15 || !colors.every((c) => c === INK || /^#[0-9a-f]{6}$/.test(c))) return null
  const cells = [...m[2]!].map((d) => parseInt(d, 16))
  if (cells.some((c) => c > colors.length)) return null
  return { colors, cells }
}

/** Serializes, keeping only the colours actually used (in first-use order). */
export function formatPixels(icon: PixelIcon): string {
  const used: string[] = []
  const cells = icon.cells.map((c) => {
    if (!c) return 0
    const color = icon.colors[c - 1]!
    let i = used.indexOf(color)
    if (i < 0) i = used.push(color) - 1
    return i + 1
  })
  return `px1;${SIDE};${(used.length ? used : [icon.colors[0] ?? '#f4f4f4']).join(',')};${cells.map((c) => c.toString(16)).join('')}`
}

/** The generated mark for `id` as an editable icon, in `color`. */
export function fromIdenticon(id: string, color: string): PixelIcon {
  const bits = identicon(id)
  const cells = new Array<number>(SIDE * SIDE).fill(0)
  bits.forEach((on, i) => {
    if (on) cells[(Math.floor(i / 5) + 1) * SIDE + (i % 5) + 1] = 1
  })
  return { colors: [color], cells }
}

export function blankPixels(color: string): PixelIcon {
  return { colors: [color], cells: new Array<number>(SIDE * SIDE).fill(0) }
}

/** Paints `color` (or erases, with null) at (x, y), mirrored across the middle column if asked. */
export function paint(icon: PixelIcon, x: number, y: number, color: string | null, mirror: boolean): PixelIcon {
  const colors = [...icon.colors]
  let value = 0
  if (color) {
    let i = colors.indexOf(color)
    if (i < 0) i = colors.push(color) - 1
    value = i + 1
  }
  const cells = [...icon.cells]
  cells[y * SIDE + x] = value
  if (mirror) cells[y * SIDE + (SIDE - 1 - x)] = value
  return { colors, cells }
}

/** Every painted colour is in `palette` (designs must use the server's palette). */
export function fitsPalette(icon: PixelIcon, palette: readonly string[]): boolean {
  return icon.cells.every((c) => !c || icon.colors[c - 1] === INK || palette.includes(icon.colors[c - 1]!))
}

export function isBlank(icon: PixelIcon): boolean {
  return icon.cells.every((c) => !c)
}
